'use client';

import { cn } from '@/lib/utils';
import { Trash2, Plus, Minus, ShoppingCart } from 'lucide-react';
import type { CartItem, Cart } from '../types';
import { formatCurrency } from '../../../types';

interface ItemCartProps {
  cart: Cart;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemoveItem: (itemId: string) => void;
  onClearCart: () => void;
  onCheckout: () => void;
}

export function ItemCart({
  cart,
  onUpdateQuantity,
  onRemoveItem,
  onClearCart,
  onCheckout,
}: ItemCartProps) {
  const isEmpty = cart.items.length === 0;

  return (
    <div className="flex flex-col h-full bg-card border-l border-border">
      {/* 헤더 */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5" />
          <h2 className="font-semibold">장바구니</h2>
          {!isEmpty && (
            <span className="px-2 py-0.5 rounded-full bg-primary text-primary-foreground text-xs">
              {cart.items.length}
            </span>
          )}
        </div>
        {!isEmpty && (
          <button
            onClick={onClearCart}
            className="text-sm text-red-500 hover:text-red-600"
          >
            전체 삭제
          </button>
        )}
      </div>

      {/* 장바구니 아이템 목록 */}
      <div className="flex-1 overflow-y-auto">
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <ShoppingCart className="w-12 h-12 mb-2 opacity-50" />
            <p>장바구니가 비어있습니다</p>
            <p className="text-sm">상품을 선택해주세요</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {cart.items.map((cartItem) => (
              <CartItemRow
                key={cartItem.item.id}
                cartItem={cartItem}
                onUpdateQuantity={onUpdateQuantity}
                onRemove={onRemoveItem}
              />
            ))}
          </div>
        )}
      </div>

      {/* 합계 및 결제 버튼 */}
      <div className="border-t border-border p-4 space-y-3">
        {/* 금액 요약 */}
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">소계</span>
            <span>{formatCurrency(cart.subtotal)}</span>
          </div>
          {cart.discount > 0 && (
            <div className="flex justify-between text-green-500">
              <span>할인</span>
              <span>-{formatCurrency(cart.discount)}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">부가세 (10%)</span>
            <span>{formatCurrency(cart.tax)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold pt-2 border-t border-border">
            <span>합계</span>
            <span className="text-primary">{formatCurrency(cart.total)}</span>
          </div>
        </div>

        {/* 결제 버튼 */}
        <button
          onClick={onCheckout}
          disabled={isEmpty}
          className={cn(
            'w-full py-3 rounded-lg font-semibold text-lg transition-colors',
            isEmpty
              ? 'bg-muted text-muted-foreground cursor-not-allowed'
              : 'bg-primary text-primary-foreground hover:bg-primary/90'
          )}
        >
          결제하기
        </button>
      </div>
    </div>
  );
}

interface CartItemRowProps {
  cartItem: CartItem;
  onUpdateQuantity: (itemId: string, quantity: number) => void;
  onRemove: (itemId: string) => void;
}

function CartItemRow({ cartItem, onUpdateQuantity, onRemove }: CartItemRowProps) {
  const { item, quantity, discount } = cartItem;
  const itemTotal = item.price * quantity;
  const discountAmount = itemTotal * (discount / 100);
  const finalPrice = itemTotal - discountAmount;

  return (
    <div className="p-3">
      <div className="flex items-start gap-3">
        {/* 상품 정보 */}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm line-clamp-1">{item.name}</p>
          <p className="text-xs text-muted-foreground">{item.code}</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm">{formatCurrency(item.price)}</span>
            {discount > 0 && (
              <span className="text-xs text-green-500">-{discount}%</span>
            )}
          </div>
        </div>

        {/* 삭제 버튼 */}
        <button
          onClick={() => onRemove(item.id)}
          className="p-1 text-muted-foreground hover:text-red-500 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* 수량 조절 및 금액 */}
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-center gap-1">
          <button
            onClick={() => onUpdateQuantity(item.id, Math.max(1, quantity - 1))}
            className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-accent"
          >
            <Minus className="w-4 h-4" />
          </button>
          <span className="w-10 text-center font-medium">{quantity}</span>
          <button
            onClick={() => onUpdateQuantity(item.id, Math.min(item.stock, quantity + 1))}
            disabled={quantity >= item.stock}
            className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center hover:bg-accent disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
          </button>
        </div>
        <span className="font-semibold">{formatCurrency(finalPrice)}</span>
      </div>
    </div>
  );
}
